import _omit from "lodash/omit";
import _sortBy from "lodash/sortBy";
import _uniq from "lodash/uniq";
import { CustomFieldContentTypeRecordType } from "pages/CustomField/CustomField.types";
import { EventRecordType } from "pages/Event/Event.types";
import { EventTypeRecordType } from "pages/EventType.types";
import { PriceRecordType, PriceTypeRecordType } from "pages/SharedRecords.types";
import { TranslateFunction } from "react-admin";

export type ExportFields = {
  [key: string]: { name: string; isChecked: boolean; tooltip?: string } | undefined;
};

export type ExportFieldsets = {
  [key: string]: { isChecked: boolean; headersPrefix?: string; fields: ExportFields } | undefined;
};

const contactInfoFieldset = (
  type: string,
  resource: string,
  translate: TranslateFunction,
  getterPrefix?: string,
  translationString?: string,
): ExportFieldsets => {
  const translatedResource = translate(translationString ? translationString : `resources.${resource}.name`, {
    smart_count: 1,
  });

  const finalGetterPrefix = getterPrefix ? `${getterPrefix}${type}` : type;
  const checkEmail = type === "main";
  const contactInfoName = translate(`resources.users.fields.${type}_contact_info`);
  const fieldsetName = getterPrefix ? `${translatedResource} - ${contactInfoName}` : contactInfoName;

  return {
    [fieldsetName]: {
      isChecked: false,
      headersPrefix: `${translatedResource} - ${contactInfoName}`,
      fields: {
        [`${finalGetterPrefix}_contact_info__email`]: {
          name: translate("resources.users.fields.contact_info.email"),
          isChecked: checkEmail,
        },
        [`${finalGetterPrefix}_contact_info__phone`]: {
          name: translate("resources.users.fields.contact_info.phone"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__mobile`]: {
          name: translate("resources.users.fields.contact_info.mobile"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__fax`]: {
          name: translate("resources.users.fields.contact_info.fax"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__organization_name`]: {
          name: translate("resources.users.fields.contact_info.organization_name"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__address_line_1`]: {
          name: translate("resources.users.fields.contact_info.address_line_1"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__address_line_2`]: {
          name: translate("resources.users.fields.contact_info.address_line_2"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__postcode`]: {
          name: translate("resources.users.fields.contact_info.postcode"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__city`]: {
          name: translate("resources.users.fields.contact_info.city"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__state`]: {
          name: translate("resources.users.fields.contact_info.state"),
          isChecked: false,
        },
        [`${finalGetterPrefix}_contact_info__countrycode`]: {
          name: translate("resources.users.fields.contact_info.country"),
          isChecked: false,
        },
      },
    },
  };
};

const getLecturerDetailsFieldsets = (translate: TranslateFunction, getterPrefix: string = ""): ExportFieldsets => {
  const mainContactInfoFieldsets = contactInfoFieldset(
    "main",
    "lecturers",
    translate,
    `${getterPrefix}main_lecturer__`,
  );

  return {
    "resources.lecturers.name": {
      isChecked: false,
      headersPrefix: translate("resources.lecturers.name", 1),
      fields: {
        [`${getterPrefix}main_lecturer__first_name`]: {
          name: translate("resources.users.fields.first_name"),
          isChecked: false,
        },
        [`${getterPrefix}main_lecturer__last_name`]: {
          name: translate("resources.users.fields.last_name"),
          isChecked: false,
        },
        [`${getterPrefix}main_lecturer__lecturer__qualifications`]: {
          name: translate("resources.users.fields.qualifications"),
          isChecked: false,
        },
        [`${getterPrefix}main_lecturer__description`]: {
          name: translate("resources.users.fields.description"),
          isChecked: false,
        },
      },
    },
    ...mainContactInfoFieldsets,
  };
};

const attendeeRoleFields = (translate: TranslateFunction, getterPrefix: string): ExportFields => ({
  [`${getterPrefix}attendee__attendee_number`]: {
    name: translate("resources.attendees.fields.attendee_number"),
    isChecked: true,
  },
  [`${getterPrefix}attendee__debit_number`]: {
    name: translate("resources.attendees.fields.debit_number"),
    isChecked: true,
  },
});

const lecturerRoleFields = (translate: TranslateFunction, getterPrefix: string): ExportFields => ({
  [`${getterPrefix ? getterPrefix : "lecturer__"}qualifications`]: {
    name: translate("resources.attendees.fields.qualifications"),
    isChecked: true,
  },
  [`${getterPrefix}description_plain_text`]: {
    name: translate("resources.attendees.fields.description"),
    isChecked: true,
  },
});

const userFieldsets =
  (resource: string, translationString?: string, isAttendee?: boolean, isLecturer?: boolean) =>
  (translate: TranslateFunction, getterPrefix: string = "", inLecturerPortal: boolean = false): ExportFieldsets => {
    const userFields: ExportFields = {
      [`${getterPrefix}title`]: { name: translate("resources.users.fields.title"), isChecked: true },
      [`${getterPrefix}first_name`]: { name: translate("resources.users.fields.first_name"), isChecked: true },
      [`${getterPrefix}last_name`]: { name: translate("resources.users.fields.last_name"), isChecked: true },
      [`${getterPrefix}birthday`]: { name: translate("resources.users.fields.birthday"), isChecked: false },
      [`${getterPrefix}gender`]: { name: translate("resources.users.fields.gender"), isChecked: false },
      [`${getterPrefix}organization_names`]: {
        name: translate("resources.users.fields.organization_names"),
        isChecked: false,
      },
      [`${getterPrefix}user_list_names`]: {
        name: translate("resources.attendees.fields.lists"),
        isChecked: false,
      },
    };

    // user, attendee and lecturer are always kept in separate fieldsets (columns) wherever a
    // user can be exported (user / attendee / lecturer / booking exports), instead of merging
    // the role fields into the user fields. Role columns only show when relevant for the export.
    const includeAttendee = isAttendee || (resource === "attendees" && !inLecturerPortal);
    const includeLecturer = isLecturer || resource === "lecturers";

    // Contact infos belong to the user, so their column headers always read "Person",
    // regardless of whether this is a user / attendee / lecturer / booking export.
    const userTranslationString = translationString ? translationString : "resources.users.name";
    const userColumnName = translate(userTranslationString, { smart_count: 1 });

    const fieldsets: ExportFieldsets = {
      [userColumnName]: {
        isChecked: false,
        headersPrefix: userColumnName,
        fields: userFields,
      },
    };

    if (includeAttendee) {
      const attendeeName = translate("resources.attendees.name", { smart_count: 1 });
      fieldsets[attendeeName] = {
        isChecked: false,
        headersPrefix: attendeeName,
        fields: attendeeRoleFields(translate, getterPrefix),
      };
    }

    if (includeLecturer) {
      const lecturerName = translate("resources.lecturers.name", { smart_count: 1 });
      fieldsets[lecturerName] = {
        isChecked: false,
        headersPrefix: lecturerName,
        fields: lecturerRoleFields(translate, getterPrefix),
      };
    }

    const contactInfoFieldsets: ExportFieldsets = {
      ...contactInfoFieldset("main", resource, translate, getterPrefix, userTranslationString),
      ...contactInfoFieldset("invoice", resource, translate, getterPrefix, userTranslationString),
      ...contactInfoFieldset("invoice_dispatch", resource, translate, getterPrefix, userTranslationString),
    };

    return { ...fieldsets, ...contactInfoFieldsets };
  };

export const attendeeFieldsets = userFieldsets("attendees");
export const lecturerFieldsets = userFieldsets("lecturers");
export { userFieldsets };

export const eventFieldsets = (
  translate: TranslateFunction,
  includeLecturerDetails: boolean,
  additionalFieldsets?: ExportFieldsets,
  getterPrefix: string = "",
  inLecturerPortal: boolean = false,
): ExportFieldsets => {
  let eventFields = {
    [`${getterPrefix}id_number`]: { name: translate("resources.events.fields.id_number"), isChecked: true },
    [`${getterPrefix}status_display`]: { name: translate("resources.events.fields.status"), isChecked: true },
    [`${getterPrefix}attendees_min`]: { name: translate("resources.events.fields.attendees_min"), isChecked: true },
    [`${getterPrefix}all_valid_bookings_count`]: {
      name: translate("export.fields.all_valid_bookings_count"),
      isChecked: true,
    },
    [`${getterPrefix}attendees_max`]: { name: translate("resources.events.fields.attendees_max"), isChecked: true },
    [`${getterPrefix}registration_deadline_display`]: {
      name: translate("resources.events.fields.registration_deadline"),
      isChecked: true,
    },
    [`${getterPrefix}external_url`]: { name: translate("resources.events.fields.external_url"), isChecked: true },
    [`${getterPrefix}rooms`]: { name: translate("resources.events.fields.rooms"), isChecked: false },
    [`${getterPrefix}first_day_local`]: { name: translate("resources.events.fields.first_day"), isChecked: true },
    [`${getterPrefix}remaining_dates`]: {
      name: translate("resources.events.fields.remaining_dates"),
      isChecked: true,
    },
    [`${getterPrefix}first_day_start_time`]: {
      name: translate("resources.events.fields.first_day_start_time"),
      isChecked: false,
    },
    [`${getterPrefix}first_day_end_time`]: {
      name: translate("resources.events.fields.first_day_end_time"),
      isChecked: false,
    },
  };

  let eventBaseFields = {
    [`${getterPrefix}event_base__name`]: { name: translate("resources.event_bases.fields.name"), isChecked: true },
    [`${getterPrefix}event_base__event_type__name`]: {
      name: translate("resources.event_bases.fields.event_type"),
      isChecked: true,
    },
    [`${getterPrefix}event_base__categories`]: {
      name: translate("resources.event_bases.fields.categories"),
      isChecked: false,
    },
    [`${getterPrefix}event_base__audiences`]: {
      name: translate("resources.event_bases.fields.audiences"),
      isChecked: false,
    },
    [`${getterPrefix}event_base__summary_plain_text`]: {
      name: translate("resources.event_bases.fields.summary"),
      isChecked: true,
    },
    [`${getterPrefix}event_base__tags`]: { name: translate("resources.event_bases.fields.tags"), isChecked: false },
    [`${getterPrefix}event_base__contexts`]: {
      name: translate("resources.event_bases.fields.contexts"),
      isChecked: false,
    },
    [`${getterPrefix}event_base__partners`]: {
      name: translate("resources.event_bases.fields.partners"),
      isChecked: false,
    },
    [`${getterPrefix}event_base__contact_persons`]: {
      name: translate("resources.event_bases.fields.contact_persons"),
      isChecked: true,
    },
  };

  if (inLecturerPortal) {
    const filteredEventFields = [`${getterPrefix}rooms`];

    const filteredEventBaseFields = [
      `${getterPrefix}event_base__tags`,
      `${getterPrefix}event_base__contexts`,
      `${getterPrefix}event_base__partners`,
      `${getterPrefix}event_base__contact_persons`,
    ];
    eventBaseFields = _omit(eventBaseFields, filteredEventBaseFields);
    eventFields = _omit(eventFields, filteredEventFields);
  }

  let fieldsets: ExportFieldsets = {
    "resources.events.name": {
      isChecked: true,
      headersPrefix: translate("resources.events.name", 1),
      fields: eventFields,
    },
    ...(additionalFieldsets || {}),
    "resources.events.fields.event_base": {
      isChecked: false,
      headersPrefix: translate("resources.events.fields.event_base"),
      fields: eventBaseFields,
    },
    "resources.events.fields.location": {
      isChecked: false,
      headersPrefix: translate("resources.event_locations.name", 1),
      fields: {
        [`${getterPrefix}location__name`]: {
          name: translate("resources.event_locations.fields.name"),
          isChecked: true,
        },
        [`${getterPrefix}location__address__postcode`]: {
          name: translate("resources.event_locations.fields.address.postcode"),
          isChecked: true,
        },
        [`${getterPrefix}location__address__city`]: {
          name: translate("resources.event_locations.fields.address.city"),
          isChecked: true,
        },
        [`${getterPrefix}location__address__address_line_1`]: {
          name: translate("resources.event_locations.fields.address.address_line_1"),
          isChecked: true,
        },
        [`${getterPrefix}location__address__address_line_2`]: {
          name: translate("resources.event_locations.fields.address.address_line_2"),
          isChecked: false,
        },
      },
    },
  };

  if (includeLecturerDetails) {
    const lecturerDetailsFieldsets = getLecturerDetailsFieldsets(translate, getterPrefix);
    fieldsets = { ...fieldsets, ...lecturerDetailsFieldsets };
  } else {
    const fieldset = fieldsets["resources.events.name"];
    if (fieldset && !inLecturerPortal) {
      fieldset.fields.lecturers = { name: translate("resources.events.fields.lecturers"), isChecked: true };
    }
  }

  return fieldsets;
};

export const eventBaseFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  const fieldsets: ExportFieldsets = {
    "resources.event_bases.name": {
      isChecked: false,
      headersPrefix: translate("resources.event_bases.name", 1),
      fields: {
        name: { name: translate("resources.event_bases.fields.name"), isChecked: true },
        event_type__name: { name: translate("resources.event_bases.fields.event_type"), isChecked: true },
        categories: { name: translate("resources.event_bases.fields.categories"), isChecked: false },
        audiences: { name: translate("resources.event_bases.fields.audiences"), isChecked: false },
        summary_plain_text: { name: translate("resources.event_bases.fields.summary"), isChecked: true },
        tags: { name: translate("resources.event_bases.fields.tags"), isChecked: false },
        contexts: { name: translate("resources.event_bases.fields.contexts"), isChecked: false },
        partners: { name: translate("resources.event_bases.fields.partners"), isChecked: false },
        contact_persons: { name: translate("resources.event_bases.fields.contact_persons"), isChecked: true },
      },
    },
  };

  return fieldsets;
};

const bookingFields = (translate: TranslateFunction) => {
  const translatedBooking = translate("resources.bookings.name", { smart_count: 1 });

  return {
    [translatedBooking]: {
      isChecked: true,
      headersPrefix: translatedBooking,
      fields: {
        status: { name: translate("resources.bookings.fields.status"), isChecked: true },
        created_at: { name: translate("resources.bookings.fields.created_at"), isChecked: true },
        booking_number: { name: translate("resources.bookings.fields.booking_number"), isChecked: true },
        price__price_type: { name: translate("resources.prices.fields.price_type"), isChecked: true },
        price__amount_local: { name: translate("resources.prices.fields.amount"), isChecked: true },
        enrollment__created_at: { name: translate("resources.enrollments.fields.created_at"), isChecked: true },
        has_attended: { name: translate("resources.event_attendances.fields.has_attended"), isChecked: true },
      },
    },
  };
};

export const bookingFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  return {
    ...bookingFields(translate),
    ...attendeeFieldsets(translate, "attendee__"),
    ...eventFieldsets(translate, false, undefined, "event__"),
  };
};

export const lecturerBookingFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  return {
    ...bookingFields(translate),
    ...attendeeFieldsets(translate, "attendee__", true),
    ...eventFieldsets(translate, false, undefined, "event__", true),
  };
};

export const bookingEventFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  return {
    ...bookingFields(translate),
    ...attendeeFieldsets(translate, "attendee__"),
  };
};

export const descriptionFieldsetsFromEventTypes = (
  translate: TranslateFunction,
  eventTypes: EventTypeRecordType[],
): ExportFieldsets => {
  const labelsPerSlug: { [key: string]: string[] | undefined } = {};
  eventTypes.forEach((eventType) => {
    eventType.description_fields.forEach((descriptionField) => {
      const slug = descriptionField.slug;
      const labels = labelsPerSlug[slug];

      if (labels === undefined) {
        labelsPerSlug[slug] = [descriptionField.label];
      } else {
        labelsPerSlug[slug] = labels.concat(descriptionField.label).sort();
      }
    });
  });

  const slugs = Object.keys(labelsPerSlug);
  if (slugs.length === 0) return {};

  const fields: ExportFields = {};
  const sortedSlugs = _sortBy(slugs, (slug) => {
    const labels = labelsPerSlug[slug];
    return typeof labels === "undefined" ? undefined : labels[0];
  });
  const slugTranslated = translate("resources.event_base_description_fields.fields.slug");

  sortedSlugs.forEach((slug) => {
    const labels = labelsPerSlug[slug];

    fields[slug] = {
      name: _uniq(labels).join(", "),
      isChecked: false,
      tooltip: `${slugTranslated}: ${slug}`,
    };
  });
  return { "resources.event_bases.fields.descriptions": { fields, isChecked: false } };
};

export const exportFieldsForCustomFieldContentType =
  (group: CustomFieldContentTypeRecordType) =>
  (prefix?: string): ExportFields => {
    const exportFields: ExportFields = {};
    const customFields = group.custom_fields;

    customFields.forEach((customField) => {
      exportFields[`${prefix ? `${prefix}__` : ""}get_custom_field_export_value_${customField.slug}`] = {
        name: customField.label,
        isChecked: false,
      };
    });

    return exportFields;
  };

export type CustomExportFieldsByResource = { [key: string]: (prefix?: string) => ExportFields };
export function enhanceWithCustomExportFields(
  fieldsets: ExportFieldsets,
  resource: string,
  customExportFieldsByResource: CustomExportFieldsByResource | null,
  translate: TranslateFunction,
): ExportFieldsets {
  if (!customExportFieldsByResource) return fieldsets;

  const fieldsetsToEnhancePropsByResource: { [key: string]: { [key: string]: string | undefined }[] | undefined } = {
    events: [
      { key: "resources.events.name", resource: "events" },
      { key: "resources.events.fields.event_base", resource: "event_bases", prefix: "event_base" },
      { key: "resources.events.fields.location", resource: "event_locations", prefix: "location" },
      { key: "resources.lecturers.name", resource: "lecturers", prefix: "main_lecturer__lecturer" },
    ],
    attendees: [
      { key: translate("resources.users.name", 1), resource: "users" },
      { key: translate("resources.attendees.name", 1), resource: "attendees", prefix: "attendee" },
    ],
    lecturers: [
      { key: translate("resources.users.name", 1), resource: "users" },
      { key: translate("resources.lecturers.name", 1), resource: "lecturers", prefix: "lecturer" },
    ],
    users: [
      { key: translate("resources.users.name", 1), resource: "users" },
      { key: translate("resources.attendees.name", 1), resource: "attendees", prefix: "attendee" },
      { key: translate("resources.lecturers.name", 1), resource: "lecturers", prefix: "lecturer" },
    ],
    organizations: [{ key: translate("resources.organizations.name", 1), resource: "organizations" }],
    event_bases: [{ key: "resources.event_bases.name", resource: "event_bases" }],
    event_locations: [{ key: "resources.event_locations.name", resource: "event_locations" }],
    bookings: [
      { key: translate("resources.bookings.name", 1), resource: "bookings" },
      { key: translate("resources.users.name", 1), resource: "users", prefix: "attendee" },
      { key: translate("resources.attendees.name", 1), resource: "attendees", prefix: "attendee__attendee" },
      { key: "resources.events.fields.location", resource: "event_locations", prefix: "event__location" },
      { key: "resources.events.name", resource: "events", prefix: "event" },
    ],
    enrollments: [
      { key: "resources.events.fields.event_base", resource: "event_bases", prefix: "event__event_base" },
      { key: "resources.events.name", resource: "events", prefix: "event" },
      { key: "resources.events.fields.location", resource: "event_locations", prefix: "event__location" },
    ],
  };
  const fieldsetsToEnhanceProps = fieldsetsToEnhancePropsByResource[resource] || [];

  fieldsetsToEnhanceProps.forEach((fieldsetProps) => {
    const fieldsetKey = fieldsetProps.key;
    const customFieldsResource = fieldsetProps.resource;

    if (!fieldsetKey || !customFieldsResource) return;

    const customFields = customExportFieldsByResource[customFieldsResource](fieldsetProps.prefix);
    const fieldsetToEnhance = fieldsets[fieldsetKey];

    if (!fieldsetToEnhance || !customFields) return;

    fieldsetToEnhance.fields = { ...fieldsetToEnhance.fields, ...customFields };
  });

  return fieldsets;
}

export function getAllPriceTypesFieldset(priceTypes: PriceTypeRecordType[], translate: TranslateFunction) {
  const fields: ExportFields = {};

  _sortBy(priceTypes, "name").forEach((priceType) => {
    fields[`get_price_amount_local_for_price_type_${priceType.id}`] = {
      name: priceType.name,
      isChecked: false,
    };
  });

  const pricesLabel = translate("resources.prices.name", 2);
  const headersPrefix = `${translate("resources.events.name", 1)} - ${pricesLabel}`;
  return { [pricesLabel]: { fields, headersPrefix, isChecked: false } };
}

export function getPriceTypeFieldset(events: EventRecordType[], translate: TranslateFunction) {
  const fields: ExportFields = {};
  let prices: PriceRecordType[] = [];

  events.forEach((e) => {
    prices = [...prices, ...e.prices];
  });

  _sortBy(prices, "price_type_name").forEach((price) => {
    fields[`get_price_amount_local_for_price_type_${price.price_type}`] = {
      name: price.price_type_name,
      isChecked: false,
    };
  });

  const pricesLabel = translate("resources.prices.name", 2);
  const headersPrefix = `${translate("resources.events.name", 1)} - ${pricesLabel}`;
  return { [pricesLabel]: { fields, headersPrefix, isChecked: false } };
}

export const userListDataFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  return {
    ...userFieldsets("users")(translate, "user__"),
  };
};

export const interestFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  return {
    ...userFieldsets("users")(translate, "user__"),
    ...eventFieldsets(translate, false, undefined, "event__"),
  };
};

const organizationFields = (translate: TranslateFunction) => {
  const translatedOrganization = translate("resources.organizations.name", { smart_count: 1 });

  return {
    [translatedOrganization]: {
      isChecked: true,
      headersPrefix: translatedOrganization,
      fields: {
        name: { name: translate("resources.organizations.fields.name"), isChecked: true },
        debit_number: { name: translate("resources.organizations.fields.debit_number"), isChecked: false },
        value_added_tax_id: { name: translate("resources.organizations.fields.value_added_tax_id"), isChecked: false },
        trade_register_number: {
          name: translate("resources.organizations.fields.trade_register_number"),
          isChecked: false,
        },
        fiscal_number: { name: translate("resources.organizations.fields.fiscal_number"), isChecked: false },
        tags: { name: translate("resources.organizations.fields.tags"), isChecked: false },
        created_at: { name: translate("resources.organizations.fields.created_at"), isChecked: false },
      },
    },
    ...contactInfoFieldset("main", "organizations", translate),
    ...contactInfoFieldset("invoice", "organizations", translate),
    ...contactInfoFieldset("invoice_dispatch", "organizations", translate),
  };
};

export const organizationFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  return {
    ...organizationFields(translate),
  };
};
const enrollmentPersonFieldset = (translate: TranslateFunction): ExportFieldsets => {
  const translatedPerson = translate("resources.enrollments.fields.user");

  return {
    [translatedPerson]: {
      isChecked: true,
      headersPrefix: translatedPerson,
      fields: {
        resolved_title: { name: translate("resources.users.fields.title"), isChecked: false },
        resolved_first_name: { name: translate("resources.users.fields.first_name"), isChecked: true },
        resolved_last_name: { name: translate("resources.users.fields.last_name"), isChecked: true },
        resolved_birthday: { name: translate("resources.users.fields.birthday"), isChecked: false },
        resolved_gender: { name: translate("resources.users.fields.gender"), isChecked: false },
      },
    },
  };
};

const enrollmentFields = (translate: TranslateFunction) => {
  const translatedEnrollment = translate("resources.enrollments.name", { smart_count: 1 });

  return {
    [translatedEnrollment]: {
      isChecked: true,
      headersPrefix: translatedEnrollment,
      fields: {
        created_at: { name: translate("resources.enrollments.fields.created_at"), isChecked: true },
        price_type_name: { name: translate("resources.prices.fields.price_type"), isChecked: true },
        price_amount: { name: translate("resources.prices.fields.amount"), isChecked: true },
        format: { name: translate("resources.enrollments.fields.format"), isChecked: false },
        note: { name: translate("resources.enrollments.fields.note"), isChecked: false },
        approval_status: { name: translate("resources.enrollments.fields.approval_status"), isChecked: true },
        approval_reason: { name: translate("resources.enrollments.fields.approval_reason"), isChecked: false },
      },
    },
  };
};

export const enrollmentFieldsets = (translate: TranslateFunction): ExportFieldsets => {
  const mainContactInfoFieldsets = contactInfoFieldset(
    "main",
    "enrollments",
    translate,
    "resolved_",
    "resources.enrollments.fields.user",
  );

  const invoiceContactInfoFieldsets = contactInfoFieldset(
    "invoice",
    "enrollments",
    translate,
    "resolved_",
    "resources.enrollments.fields.user",
  );

  const invoiceDispatchContactInfoFieldsets = contactInfoFieldset(
    "invoice_dispatch",
    "enrollments",
    translate,
    "resolved_",
    "resources.enrollments.fields.user",
  );

  return {
    ...enrollmentFields(translate),
    ...enrollmentPersonFieldset(translate),
    ...mainContactInfoFieldsets,
    ...invoiceContactInfoFieldsets,
    ...invoiceDispatchContactInfoFieldsets,
    ...eventFieldsets(translate, false, undefined, "event__"),
  };
};
